import type {
  DeliveryItems,
  DeliveryList,
  ReceiveApiError as ApiError,
} from '@/api';
import { ReceiveShipmentsService } from '@/api';
import { SearchPartialDeliveryVarianceResponse } from '@/api/receive';

import type { RootState } from '@/app/rootReducer';
import type { Process } from '@/hooks/useHandleProcess';
import { ReceivingStatus } from '@/pages/Receiving/PageReceiving/types';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

export type ReceivingProcess = Extract<Process, 'RCC' | 'RCP'>;
export const ReceivingProcessConst: Record<'RCC' | 'RCP', ReceivingProcess> = {
  RCC: 'RCC',
  RCP: 'RCP',
};

export interface DeliveryListSlice {
  deliveryItems: DeliveryItems[];
  selectedDeliveries: DeliveryItems[];
  deliveryProcess: ReceivingProcess | undefined;
  isLoading: boolean;
  error?: ApiError;
}

type DeliveryFilters = {
  fromDate?: string;
  endDate?: string;
  deliveryNumber?: string;
  status?: ReceivingStatus;
};

const initialState: DeliveryListSlice = {
  deliveryItems: [],
  selectedDeliveries: [],
  deliveryProcess: undefined,
  isLoading: false,
  error: undefined,
};

export const getDeliveryList = createAsyncThunk<
  DeliveryList | undefined,
  DeliveryFilters | undefined,
  { state: RootState; rejectValue: ApiError }
>(
  'deliveries/getDeliveryList',
  async (deliveryFilters, { getState, rejectWithValue }) => {
    const { currentStore } = getState();

    if (currentStore.store?.storeCode) {
      const { storeCode } = currentStore.store;

      if (storeCode) {
        try {
          return await ReceiveShipmentsService.receiveshipGetDeliveryList({
            storeId: storeCode,
            ...deliveryFilters,
          });
        } catch (error) {
          return rejectWithValue(error);
        }
      }
    }
  }
);

export const unlockDeliveries = createAsyncThunk<
  void,
  void,
  { rejectValue: ApiError; state: RootState }
>('deliveries/unlockDeliveries', async (_, { getState, rejectWithValue }) => {
  const {
    deliveries: { selectedDeliveries },
  } = getState();

  let selectedDeliveriesCodes = selectedDeliveries.map(
    ({ codeDelivery }) => codeDelivery
  );
  const partialDeliveries = selectedDeliveries.filter(
    delivery => delivery.status === ReceivingStatus.PARTIAL
  );

  const nonPartialDeliveries = selectedDeliveries.filter(
    delivery => delivery.status !== ReceivingStatus.PARTIAL
  );

  const deliveriesInStorage = sessionStorage.getItem('selectedDeliveries');

  if (deliveriesInStorage) {
    selectedDeliveriesCodes = (
      JSON.parse(deliveriesInStorage) as DeliveryItems[]
    ).map(({ codeDelivery }) => codeDelivery);
  }

  if (selectedDeliveriesCodes.length > 0) {
    try {
      partialDeliveries?.length > 0
      &&
      await ReceiveShipmentsService.receiveshipChangeDeliveryStatus({
        requestBody: {
          deliveryNumbers: partialDeliveries.map(del => del.codeDelivery),
          status: ReceivingStatus.PARTIAL,
        },
      });
      nonPartialDeliveries?.length > 0
      &&
      await ReceiveShipmentsService.receiveshipChangeDeliveryStatus({
        requestBody: {
          deliveryNumbers: nonPartialDeliveries.map(del => del.codeDelivery),
          status: ReceivingStatus.NEW,
        },
      });
    } catch (error) {
      return rejectWithValue(error as ApiError);
    }
  }
});

export const unlockSpecificDeliveries = createAsyncThunk<
  void,
  {
    deliveryCodes: string[];
  },
  { rejectValue: ApiError; state: RootState }
>(
  'deliveries/unlockSpecificDeliveries',
  async ({ deliveryCodes }, { rejectWithValue }) => {
    try {
      let variances: SearchPartialDeliveryVarianceResponse[] = [];
      try {
        variances = await Promise.all(
          deliveryCodes.map(deliveryCode =>
            ReceiveShipmentsService.receiveshipSearchPartialDeliveryVariance({
              deliveryNumber: deliveryCode,
            })
          )
        );
      } catch (error) {
        if (error.status === 400) {
          variances = [];
        }
      }

      const partialDeliveryNumbers = new Set(
        variances
          .map(variance => variance.deliveryNumber)
          .filter((deliveryNumber): deliveryNumber is string =>
            Boolean(deliveryNumber)
          )
      );

      const partialDeliveryCodes = deliveryCodes.filter(deliveryCode =>
        partialDeliveryNumbers.has(deliveryCode)
      );

      const newDeliveryCodes = deliveryCodes.filter(
        deliveryCode => !partialDeliveryNumbers.has(deliveryCode)
      );

      const statusChangePromises = [];

      if (partialDeliveryCodes.length > 0) {
        statusChangePromises.push(
          ReceiveShipmentsService.receiveshipChangeDeliveryStatus({
            requestBody: {
              deliveryNumbers: partialDeliveryCodes,
              status: ReceivingStatus.PARTIAL,
            },
          })
        );
      }

      if (newDeliveryCodes.length > 0) {
        statusChangePromises.push(
          ReceiveShipmentsService.receiveshipChangeDeliveryStatus({
            requestBody: {
              deliveryNumbers: newDeliveryCodes,
              status: ReceivingStatus.NEW,
            },
          })
        );
      }
      await Promise.all(statusChangePromises);
    } catch (error) {
      return rejectWithValue(error as ApiError);
    }
  }
);

const deliveriesSlice = createSlice({
  name: 'deliveries',
  initialState,
  reducers: {
    selectDelivery: (
      state: DeliveryListSlice,
      { payload }: PayloadAction<DeliveryItems>
    ) => {
      sessionStorage.setItem(
        'selectedDeliveries',
        JSON.stringify([...state.selectedDeliveries, payload])
      );

      state.selectedDeliveries.push(payload);
    },
    deselectDelivery: (
      state: DeliveryListSlice,
      action: PayloadAction<DeliveryItems>
    ) => {
      const index = state.selectedDeliveries.findIndex(
        delivery => delivery.codeDelivery === action.payload.codeDelivery
      );

      if (index !== -1) {
        state.selectedDeliveries.splice(index || 0, 1);

        sessionStorage.setItem(
          'selectedDeliveries',
          JSON.stringify(state.selectedDeliveries)
        );
      }
    },
    initDeliveryList: () => {
      sessionStorage.removeItem('selectedDeliveries');
      return initialState;
    },
    setDeliveryProcess: (
      state: DeliveryListSlice,
      action: PayloadAction<ReceivingProcess | undefined>
    ) => {
      state.deliveryProcess = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getDeliveryList.pending, state => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(getDeliveryList.fulfilled, (state, { payload }) => {
        if (payload?.deliveryItems) {
          state.deliveryItems = payload.deliveryItems;
        }

        state.isLoading = false;
        state.error = undefined;
      })
      .addCase(getDeliveryList.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload;
      });
  },
});

export const {
  selectDelivery,
  deselectDelivery,
  initDeliveryList,
  setDeliveryProcess,
} = deliveriesSlice.actions;
export default deliveriesSlice.reducer;
